import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { PageBody, PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, FileText, IndianRupee, Loader2, ExternalLink, AlertCircle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Party360({ partyType }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const isCustomer = partyType === "customer";
    const baseUrl = isCustomer ? `/customers/${id}/360` : `/vendors/${id}/360`;

    useEffect(() => {
        setLoading(true);
        api.get(baseUrl).then(({ data }) => setData(data)).finally(() => setLoading(false));
    }, [baseUrl]);

    if (loading) {
        return (
            <PageBody>
                <div className="text-center text-muted-foreground py-12 flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading 360°…
                </div>
            </PageBody>
        );
    }
    if (!data) {
        return (
            <PageBody>
                <div className="text-center text-muted-foreground py-12">Not found.</div>
            </PageBody>
        );
    }

    const { party, kpis, trend_12m, aging, recent_docs, recent_payments } = data;
    const docTypeLabel = isCustomer ? "Invoices" : "Bills";
    const partyKicker = isCustomer ? "CUSTOMER" : "VENDOR";

    return (
        <>
            <PageHeader
                kicker={partyKicker + " · 360°"}
                title={party.name}
                subtitle={[party.gstin, party.email, party.phone].filter(Boolean).join(" · ") || "Party 360 view"}
                actions={
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="party360-back">
                            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />Back
                        </Button>
                    </div>
                }
            />
            <PageBody>
                {/* KPI strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                    <KpiCard label="Lifetime" value={kpis.lifetime} testid="party360-kpi-lifetime" />
                    <KpiCard label="Outstanding" value={kpis.outstanding} tone={kpis.outstanding > 0 ? "muted" : "default"} testid="party360-kpi-outstanding" />
                    <KpiCard label="Overdue" value={kpis.overdue} tone={kpis.overdue > 0 ? "destructive" : "default"} testid="party360-kpi-overdue" />
                    <KpiCard
                        label="Avg payment days"
                        value={kpis.avg_payment_days}
                        format="number"
                        suffix=" days"
                        testid="party360-kpi-avg-days"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* 12-month trend */}
                    <Card className="lg:col-span-2 p-5" data-testid="party360-trend">
                        <div className="flex items-baseline justify-between mb-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.16em] text-gold font-semibold">12-MONTH TREND</div>
                                <div className="font-display text-lg font-light tracking-tight mt-0.5">{isCustomer ? "Sales to this customer" : "Bills from this vendor"}</div>
                            </div>
                        </div>
                        <div className="h-[260px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={trend_12m}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                                    <ReTooltip formatter={(v) => fmtMoney(v)} />
                                    <Line type="monotone" dataKey="amount" stroke="#a68a56" strokeWidth={2} dot={{ r: 3, fill: "#a68a56" }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    {/* Aging */}
                    <Card className="p-5" data-testid="party360-aging">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-gold font-semibold">{isCustomer ? "RECEIVABLES AGING" : "PAYABLES AGING"}</div>
                        <div className="mt-3 space-y-2">
                            {[
                                { l: "Current", k: "current" },
                                { l: "1–30 days", k: "d1_30" },
                                { l: "31–60 days", k: "d31_60" },
                                { l: "61–90 days", k: "d61_90" },
                                { l: "90+ days", k: "d90_plus", danger: true },
                            ].map((r) => (
                                <div key={r.k} className="flex items-baseline justify-between text-[13px]">
                                    <span className="text-muted-foreground">{r.l}</span>
                                    <span className={`font-mono tabular-nums ${r.danger && aging[r.k] > 0 ? "text-destructive font-medium" : ""}`} data-testid={`party360-aging-${r.k}`}>{fmtMoney(aging[r.k])}</span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 pt-3 border-t border-border flex items-baseline justify-between text-[14px]">
                            <span className="font-medium">Total outstanding</span>
                            <span className="font-mono tabular-nums font-medium">{fmtMoney(kpis.outstanding)}</span>
                        </div>
                        {kpis.overdue > 0 && (
                            <div className="mt-3 px-3 py-2 rounded bg-destructive/10 text-destructive text-[12px] flex items-start gap-2">
                                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                <div>{fmtMoney(kpis.overdue)} of the outstanding is past due. Consider sending a reminder.</div>
                            </div>
                        )}
                    </Card>
                </div>

                {/* Recent docs + payments */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
                    <Card className="p-5" data-testid="party360-recent-docs">
                        <div className="flex items-baseline justify-between mb-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.16em] text-gold font-semibold">RECENT {docTypeLabel.toUpperCase()}</div>
                                <div className="font-display text-lg font-light tracking-tight mt-0.5">{kpis.doc_count} total · {kpis.open_doc_count} open</div>
                            </div>
                            <Link to={isCustomer ? "/sales/invoices" : "/purchases/bills"} className="text-[11px] text-gold hover:underline">View all →</Link>
                        </div>
                        {recent_docs.length === 0 ? (
                            <div className="text-muted-foreground text-sm py-6 text-center">No {docTypeLabel.toLowerCase()} yet.</div>
                        ) : (
                            <table className="w-full text-[12.5px]">
                                <thead className="border-b border-border">
                                    <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
                                        <th className="py-2">Number</th>
                                        <th className="py-2">Date</th>
                                        <th className="py-2 text-right">Amount</th>
                                        <th className="py-2 text-right">Balance</th>
                                        <th className="py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recent_docs.slice(0, 10).map((d) => (
                                        <tr key={d.id} className="border-b border-border last:border-0">
                                            <td className="py-2 font-medium">{d.number}</td>
                                            <td className="py-2 text-muted-foreground">{d.date}</td>
                                            <td className="py-2 text-right font-mono tabular-nums">{fmtMoney(d.total, d.currency)}</td>
                                            <td className={`py-2 text-right font-mono tabular-nums ${d.balance_due > 0 ? "text-destructive" : "text-muted-foreground"}`}>{fmtMoney(d.balance_due, d.currency)}</td>
                                            <td className="py-2 text-right">
                                                <Link to={isCustomer ? `/sales/invoices/${d.id}` : `/purchases/bills/${d.id}`} className="text-muted-foreground hover:text-gold inline-flex items-center" data-testid={`party360-doc-link-${d.id}`}>
                                                    <ExternalLink className="w-3.5 h-3.5" />
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Card>

                    <Card className="p-5" data-testid="party360-recent-payments">
                        <div className="flex items-baseline justify-between mb-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.16em] text-gold font-semibold">RECENT PAYMENTS</div>
                                <div className="font-display text-lg font-light tracking-tight mt-0.5">Payment history</div>
                            </div>
                        </div>
                        {recent_payments.length === 0 ? (
                            <div className="text-muted-foreground text-sm py-6 text-center">No payments yet.</div>
                        ) : (
                            <table className="w-full text-[12.5px]">
                                <thead className="border-b border-border">
                                    <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
                                        <th className="py-2">Number</th>
                                        <th className="py-2">Date</th>
                                        <th className="py-2">Mode</th>
                                        <th className="py-2 text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recent_payments.slice(0, 10).map((p) => (
                                        <tr key={p.id} className="border-b border-border last:border-0">
                                            <td className="py-2 font-medium">{p.number || "—"}</td>
                                            <td className="py-2 text-muted-foreground">{p.date}</td>
                                            <td className="py-2 text-muted-foreground">{p.mode || "—"}</td>
                                            <td className="py-2 text-right font-mono tabular-nums">{fmtMoney(p.amount, p.currency)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Card>
                </div>
            </PageBody>
        </>
    );
}

function KpiCard({ label, value, tone = "default", testid, format = "money", suffix = "" }) {
    const formatted = format === "money"
        ? fmtMoney(value || 0)
        : (value == null ? "—" : `${value}${suffix}`);
    const toneCls = tone === "destructive" ? "text-destructive" : tone === "muted" ? "text-amber-700" : "";
    return (
        <Card className="p-4" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">{label}</div>
            <div className={`font-display text-2xl font-light tracking-tight mt-1 ${toneCls}`}>{formatted}</div>
        </Card>
    );
}
